import { createTheme } from '@mui/material/styles'
import '@mui/x-data-grid/themeAugmentation'

declare module '@mui/material/styles' {
  interface TypographyVariants {
    pageTitle: React.CSSProperties
    sectionTitle: React.CSSProperties
    cardTitle: React.CSSProperties
    fieldLabel: React.CSSProperties
    fieldValue: React.CSSProperties
    smallText: React.CSSProperties
    link: React.CSSProperties
  }
  interface TypographyVariantsOptions {
    pageTitle?: React.CSSProperties
    sectionTitle?: React.CSSProperties
    cardTitle?: React.CSSProperties
    fieldLabel?: React.CSSProperties
    fieldValue?: React.CSSProperties
    smallText?: React.CSSProperties
    link?: React.CSSProperties
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    pageTitle: true
    sectionTitle: true
    cardTitle: true
    fieldLabel: true
    fieldValue: true
    smallText: true
    link: true
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    primary: true
    secondary: true
    ghost: true
    link: true
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsVariantOverrides {
    success: true
    error: true
    warning: true
    info: true
    neutral: true
  }
}

// =============================================================================
// COLOR PALETTE
// =============================================================================
export const colors = {
  // Brand colors
  brand: {
    primary: '#153CB7',
    primaryLight: '#E8EBF8',
    primaryHover: '#1245D1'
  },
  // Text colors
  text: {
    primary: '#1F2937',
    secondary: '#6B7280',
    muted: '#9CA3AF',
    dark: '#1D1D1D',
    black: '#000000',
    white: '#FFFFFF'
  },
  // Background colors
  background: {
    default: '#F9FAFB',
    paper: '#FFFFFF',
    surface: '#FAFBFC',
    highlight: '#E8EBF8'
  },
  // Border colors
  border: {
    default: '#E5E7EB',
    hover: '#D1D5DB',
    light: '#D1D9E2'
  },
  // Status colors
  status: {
    success: '#388E10',
    successLight: '#EDF7ED',
    error: '#FF4D1A',
    errorLight: '#FDF1EE',
    errorAlt: '#EF4444',
    warning: '#CC8800',
    warningLight: '#FFF1D6',
    info: '#153CB7',
    infoLight: '#E8EBF8',
    infoBorder: '#153CB7'
  },
  // Highlight colors (for diff/comparison cells)
  highlight: {
    bg: '#FDF8EC',
    border: '#F5E6B8'
  },
  // Grey scale
  grey: {
    50: '#F9FAFB',
    100: '#F3F4F6',
    200: '#E5E7EB',
    300: '#D1D5DB',
    400: '#9CA3AF',
    500: '#6B7280',
    600: '#374151'
  }
} as const

// =============================================================================
// TYPOGRAPHY
// =============================================================================
export const fontSizes = {
  xs: '11px',
  sm: '12px',
  md: '13px',
  base: '14px',
  lg: '16px',
  xl: '20px',
  '2xl': '24px',
  '3xl': '32px'
} as const

export const fontWeights = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700
} as const

// =============================================================================
// SPACING & SIZING
// =============================================================================
export const borderRadius = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '20px',
  full: '50%'
} as const

// =============================================================================
// SHADOWS
// =============================================================================
export const shadows = {
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  md: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  lg: '0 2px 4px rgba(0, 0, 0, 0.05)',
  hover: '0 2px 4px rgba(0, 0, 0, 0.05)'
} as const

// =============================================================================
// THEME CREATION
// =============================================================================
export const theme = createTheme({
  palette: {
    primary: {
      main: colors.brand.primary,
      light: colors.brand.primaryLight
    },
    secondary: {
      main: colors.text.primary
    },
    success: {
      main: colors.status.success,
      light: colors.status.successLight,
      contrastText: colors.status.success
    },
    error: {
      main: colors.status.error,
      light: colors.status.errorLight,
      contrastText: colors.status.error
    },
    warning: {
      main: colors.status.warning,
      light: colors.status.warningLight,
      contrastText: colors.status.warning
    },
    background: {
      default: colors.background.default,
      paper: colors.background.paper
    },
    text: {
      primary: colors.text.primary,
      secondary: colors.text.secondary
    },
    divider: colors.border.default,
    grey: colors.grey
  },
  typography: {
    fontFamily: [
      'Poppins',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif'
    ].join(','),
    // Custom variants
    pageTitle: {
      fontSize: fontSizes['3xl'],
      fontWeight: fontWeights.semibold,
      lineHeight: '133%',
      letterSpacing: 0,
      color: colors.text.primary
    },
    sectionTitle: {
      fontSize: fontSizes.lg,
      fontWeight: fontWeights.semibold,
      lineHeight: 1.5,
      color: colors.text.primary
    },
    cardTitle: {
      fontSize: fontSizes.lg,
      fontWeight: fontWeights.semibold,
      lineHeight: 1.4,
      color: colors.text.primary
    },
    fieldLabel: {
      fontSize: fontSizes.sm,
      fontWeight: fontWeights.regular,
      lineHeight: 1.5,
      color: colors.text.muted
    },
    fieldValue: {
      fontSize: fontSizes.base,
      fontWeight: fontWeights.medium,
      lineHeight: 1.5,
      color: colors.text.primary
    },
    smallText: {
      fontSize: fontSizes.md,
      fontWeight: fontWeights.regular,
      lineHeight: 1.5,
      color: colors.text.secondary
    },
    link: {
      fontSize: fontSizes.base,
      fontWeight: fontWeights.medium,
      color: colors.brand.primary,
      cursor: 'pointer'
    },
    // Standard MUI variants (keep for backwards compatibility)
    h1: {
      fontSize: fontSizes['3xl'],
      fontWeight: fontWeights.semibold,
      lineHeight: '133%',
      letterSpacing: 0,
      color: colors.text.primary
    },
    h2: {
      fontSize: fontSizes.lg,
      fontWeight: fontWeights.semibold,
      lineHeight: 1.5,
      color: colors.text.primary
    },
    h3: {
      fontSize: fontSizes.lg,
      fontWeight: fontWeights.semibold,
      lineHeight: 1.4,
      color: colors.text.primary
    },
    h4: {
      fontSize: '2rem',
      fontWeight: fontWeights.semibold,
      lineHeight: '40px',
      color: colors.text.primary
    },
    h5: {
      fontWeight: fontWeights.semibold,
      color: colors.text.primary
    },
    h6: {
      fontWeight: fontWeights.semibold,
      color: colors.grey[600]
    },
    body1: {
      fontSize: fontSizes.base,
      lineHeight: '24px',
      fontWeight: fontWeights.regular,
      color: colors.grey[600]
    },
    body2: {
      fontSize: fontSizes.md,
      lineHeight: '24px',
      fontWeight: fontWeights.regular,
      color: colors.text.secondary
    },
    subtitle1: {
      fontSize: fontSizes.sm,
      fontWeight: fontWeights.regular,
      lineHeight: 1.5,
      color: colors.text.muted
    },
    subtitle2: {
      fontSize: fontSizes.xs,
      fontWeight: fontWeights.medium,
      lineHeight: 1.5,
      color: colors.text.secondary
    },
    caption: {
      fontSize: fontSizes.xs,
      fontWeight: fontWeights.medium,
      color: colors.text.secondary
    },
    button: {
      fontSize: fontSizes.base,
      fontWeight: fontWeights.medium,
      textTransform: 'none' as const
    }
  },
  shape: {
    borderRadius: 12
  },
  components: {
    // =========================================================================
    // BUTTON
    // =========================================================================
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: borderRadius.lg,
          fontWeight: fontWeights.medium
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: shadows.md
          }
        },
        sizeSmall: {
          padding: '6px 12px',
          fontSize: fontSizes.sm
        },
        sizeMedium: {
          padding: '10px 20px',
          fontSize: fontSizes.base
        },
        sizeLarge: {
          padding: '12px 24px',
          fontSize: fontSizes.base
        }
      },
      variants: [
        // Primary button - Main CTA (Save, Submit, etc.)
        {
          props: { variant: 'primary' },
          style: {
            backgroundColor: colors.brand.primary,
            color: colors.background.paper,
            '&:hover': {
              backgroundColor: colors.brand.primaryHover
            },
            '&:disabled': {
              backgroundColor: '#8A9EDC',
              color: colors.text.white
            }
          }
        },
        // Secondary button - Secondary actions
        {
          props: { variant: 'secondary' },
          style: {
            backgroundColor: colors.background.paper,
            color: colors.text.primary,
            border: `1px solid ${colors.border.default}`,
            '&:hover': {
              backgroundColor: colors.background.surface,
              borderColor: colors.border.hover
            }
          }
        },
        // Ghost button - Cancel, dismiss actions
        {
          props: { variant: 'ghost' },
          style: {
            backgroundColor: 'transparent',
            color: colors.text.secondary,
            border: 'none',
            padding: 0,
            minWidth: 'auto',
            '&:hover': {
              backgroundColor: 'transparent',
              color: colors.text.primary
            }
          }
        },
        // Link button - Looks like a link with optional icon
        {
          props: { variant: 'link' },
          style: {
            backgroundColor: 'transparent',
            color: colors.brand.primary,
            padding: 0,
            minWidth: 'auto',
            fontWeight: fontWeights.medium,
            '&:hover': {
              backgroundColor: 'transparent',
              textDecoration: 'underline'
            }
          }
        }
      ]
    },
    // =========================================================================
    // PAPER
    // =========================================================================
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.lg,
          backgroundImage: 'none',
          boxShadow: shadows.md
        },
        elevation0: {
          boxShadow: 'none'
        },
        elevation1: {
          boxShadow: shadows.md
        }
      },
      variants: [
        {
          props: { variant: 'outlined' },
          style: {
            border: `1px solid ${colors.border.default}`,
            boxShadow: 'none'
          }
        }
      ]
    },
    // =========================================================================
    // CHIP
    // =========================================================================
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: fontWeights.medium,
          borderRadius: borderRadius.md
        },
        sizeSmall: {
          height: '22px',
          fontSize: fontSizes.xs,
          '& .MuiChip-label': {
            paddingLeft: '12px',
            paddingRight: '12px'
          }
        },
        sizeMedium: {
          height: '24px',
          fontSize: fontSizes.sm,
          '& .MuiChip-label': {
            paddingLeft: '12px',
            paddingRight: '12px'
          }
        }
      },
      variants: [
        // Success status chip
        {
          props: { variant: 'success' },
          style: {
            backgroundColor: colors.status.successLight,
            color: colors.status.success,
            border: `1px solid ${colors.status.success}`
          }
        },
        // Error status chip
        {
          props: { variant: 'error' },
          style: {
            backgroundColor: colors.status.errorLight,
            color: colors.status.error,
            border: `1px solid ${colors.status.error}`
          }
        },
        // Warning status chip
        {
          props: { variant: 'warning' },
          style: {
            backgroundColor: colors.status.warningLight,
            color: colors.status.warning,
            border: `1px solid ${colors.status.warning}`
          }
        },
        // Info status chip
        {
          props: { variant: 'info' },
          style: {
            backgroundColor: colors.status.infoLight,
            color: colors.status.info,
            border: `1px solid ${colors.status.infoBorder}`
          }
        },
        // Neutral chip (outlined)
        {
          props: { variant: 'neutral' },
          style: {
            backgroundColor: 'transparent',
            color: colors.grey[600],
            border: `1px solid ${colors.border.hover}`
          }
        }
      ]
    },
    // =========================================================================
    // TYPOGRAPHY
    // =========================================================================
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          pageTitle: 'h1',
          sectionTitle: 'h2',
          cardTitle: 'h3',
          fieldLabel: 'span',
          fieldValue: 'span',
          smallText: 'p',
          link: 'span'
        }
      }
    },
    // =========================================================================
    // ICON BUTTON
    // =========================================================================
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: colors.text.secondary,
          '&:hover': {
            backgroundColor: colors.background.surface
          }
        },
        colorPrimary: {
          color: colors.brand.primary
        }
      }
    },
    // =========================================================================
    // TEXT FIELD
    // =========================================================================
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: colors.border.default
            },
            '&:hover fieldset': {
              borderColor: colors.border.hover
            },
            '&.Mui-focused fieldset': {
              borderColor: colors.brand.primary
            }
          }
        }
      }
    },
    // =========================================================================
    // SELECT
    // =========================================================================
    MuiSelect: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.border.default
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.border.hover
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.brand.primary
          }
        }
      }
    },
    MuiDataGrid: {
      defaultProps: {
        slotProps: {
          toolbar: {
            csvOptions: { disableToolbarButton: true },
            printOptions: { disableToolbarButton: true }
          }
        }
      },
      styleOverrides: {
        columnHeader: ({ theme }) => ({
          backgroundColor: theme.palette.primary.light
        }),
        row: {
          borderBottom: `1px solid ${colors.border.default}`
        }
      }
    },
    // =========================================================================
    // SNACKBAR & ALERT
    // =========================================================================
    MuiSnackbar: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.lg
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          minWidth: '600px',
          height: '75px',
          borderRadius: borderRadius.xs,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        },
        action: {
          padding: '0',
          color: '#1E4620'
        },
        message: {
          fontSize: fontSizes.base,
          fontWeight: fontWeights.bold,
          lineHeight: '143%',
          letterSpacing: '0.17px'
        },
        standardSuccess: {
          backgroundColor: colors.status.successLight,
          color: colors.status.success
        },
        standardError: {
          backgroundColor: colors.status.errorLight,
          color: colors.status.error
        },
        standardWarning: {
          backgroundColor: colors.status.warningLight,
          color: colors.status.warning
        }
      }
    }
  }
})

// =============================================================================
// CUSTOM SX PRESETS (for common patterns)
// =============================================================================
export const sxPresets = (cardHeight?: number) =>
  ({
    // Card styles
    card: {
      default: {
        border: `1px solid ${colors.border.default}`,
        borderRadius: borderRadius.lg,
        overflow: 'hidden'
      },
      clickable: {
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: colors.border.hover,
          boxShadow: shadows.hover
        }
      },
      highlighted: {
        border: `1px solid ${colors.brand.primary}`,
        bgcolor: colors.brand.primaryLight
      }
    },
    // Section styles
    section: {
      header: {
        p: 3,
        pb: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      },
      content: {
        bgcolor: colors.background.surface,
        px: 3,
        py: 2,
        pb: 3,
        borderBottomLeftRadius: borderRadius.lg,
        borderBottomRightRadius: borderRadius.lg
      }
    },
    // Grid layouts
    grid: {
      threeColumn: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 2
      },
      twoColumn: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: 2
      },
      fourColumn: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 3
      }
    },
    // Scrollable container
    // cardHeight can be customized because different card types have varying heights
    // (e.g., patient cards ~200px, order cards ~220px, medication cards ~209px)
    scrollable: {
      twoRows: {
        maxHeight: `calc(2 * (${cardHeight ?? 209}px + 16px))`,
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          width: '8px'
        },
        '&::-webkit-scrollbar-track': {
          bgcolor: colors.grey[100],
          borderRadius: borderRadius.xs
        },
        '&::-webkit-scrollbar-thumb': {
          bgcolor: colors.brand.primary,
          borderRadius: borderRadius.xs
        }
      }
    },
    // Expand button
    expandButton: {
      color: colors.text.secondary,
      transition: 'transform 0.3s'
    }
  }) as const
